import UserDataService from "@/services/userDataService";
import store from "../store";
import {environmentConfig} from "@/config/enviromentConfig";
import upstreamHeaderEnrichedLogin from "@/functions/upstreamHeaderEnrichedLogin";
import LoginService from "@/services/loginService";
import TelkomService from "@/services/telkomService";
import cleanTelkomUser from "@/functions/cleanTelkomUser";
import SafaricomService from "@/services/safaricomService";
import cleanSafariComUser from "@/functions/cleanSafariComUser";
import DcbService from "@/services/dcbService";
import {setBrandConfig} from "@/functions/setBrandConfig";

/**
 * TEMP (saf-ke-en): hardcoded test MSISDN — ?msisdn= this number skips subscription and uses in-app bypass.
 * When removing: delete this constant, digitsOnlyMsisdn(), safKeEnBypassUser / safKeEnBypassLandingStats if unused,
 * and the hardcodedKeUrlBypass branch in safariComFlow (keep the normal Safaricom flow below).
 */
const SAF_KE_EN_URL_BYPASS_MSISDN = '254722843916';

function digitsOnlyMsisdn(value) {
    if (value == null || value === '') return '';
    return String(value).replace(/\D/g, '');
}

export async function userAuthenticate(to, from, next) {
    const allowedRoutes = ['TermsConditions'];
    if (allowedRoutes.includes(to.name)) {
        store.commit('setIsLoading', false);
        return next();
    }
    if (!store.state.brandConfig) setBrandConfig();
    if (checkUserState()) return next();
    if (store.state.brandConfig.brand === 'tlk-za-en') return await telkomFlow(to, from, next);
    if (store.state.brandConfig.brand === 'saf-ke-en') return await safariComFlow(to, from, next);
    if (to.fullPath === '/login' || to.fullPath === '/otp') return next();
    store.commit('setIsLoading', true);

    const urlParams = new URLSearchParams(window.location.search);
    const chnl = urlParams.get(`chnl`)
    if (chnl) localStorage.setItem('channel', chnl)
    const localStorageToken = localStorage.getItem('token');
    const encryptedMsisdn = urlParams.get('encryptedMsisdn');
    const hasUuid = urlParams.has('uuid');
    const uuid = urlParams.get('uuid');
    if (hasUuid) store.commit('setHeaderEnrichmentAttempted', true);
    const fantasyToken = urlParams.get('fantasyToken') ? urlParams.get('fantasyToken') : localStorageToken ? localStorageToken : undefined;
    if (encryptedMsisdn) await encryptedMsisdnAuth(encryptedMsisdn, next, to);
    if (fantasyToken) return await setUserData(fantasyToken, to, next);

    attemptEncryptionRedirect(uuid);
    if (uuid) await upstreamEnrichedUserLogin(uuid, next, to);
    else redirectToLogin(next);
}

function safKeEnBypassLandingStats() {
    return {
        userStats: {
            totalSeasonPoints: 0,
            roundsPreviouslyEntered: 0,
            totalTopHundred: 0,
            airtimeWon: 0,
            roundsEntered: 0,
        },
        topManagers: [],
        userTopPicks: [],
    };
}

function safKeEnBypassUser(msisdn) {
    const bc = store.state.brandConfig;
    return {
        msisdn,
        status: 'Subscribed',
        scheme: 'Daily',
        revenuePartnerId: bc.revenuePartnerId,
        grantAccess: true,
        userAccessId: bc.userAccessId,
        username: `Bypass ${String(msisdn).slice(-4)}`,
        optInServiceMsgs: true,
        automatedEntries: false,
        loginPromo: false,
        offerCode: 'BYPASS',
        channelId: 0,
    };
}

async function safariComFlow(to, from, next) {
    const urlParams = new URLSearchParams(window.location.search);
    const urlMsisdnParam = urlParams.get('msisdn');
    const junkValues = ['undefined', 'null', 'none', 'nan'];
    const urlMsisdnRaw = urlMsisdnParam && !junkValues.includes(urlMsisdnParam.toLowerCase()) ? urlMsisdnParam : null;
    const storedMsisdn = sessionStorage.getItem('msisdn');
    const urlDigits = digitsOnlyMsisdn(urlMsisdnRaw);
    const storedDigits = digitsOnlyMsisdn(storedMsisdn);
    // TEMP: see SAF_KE_EN_URL_BYPASS_MSISDN — remove with that bypass.
    const hardcodedKeUrlBypass =
        urlDigits === SAF_KE_EN_URL_BYPASS_MSISDN ||
        storedDigits === SAF_KE_EN_URL_BYPASS_MSISDN;

    if (hardcodedKeUrlBypass) {
        const msisdn = SAF_KE_EN_URL_BYPASS_MSISDN;
        sessionStorage.setItem('msisdn', msisdn);
        store.commit('setUser', safKeEnBypassUser(msisdn));
        try {
            const stats = await UserDataService.getUserStats(msisdn);
            store.commit('setUserStats', stats.data);
        } catch {
            store.commit('setUserStats', safKeEnBypassLandingStats());
        }
        if (urlMsisdnRaw) return next({name: 'LandingPage'});
        if (to.fullPath === '/') return next({name: 'LandingPage'});
        return next();
    }

    // Priority: sessionStorage > plain ?msisdn= > header enrichment
    let msisdn = storedMsisdn || urlMsisdnRaw;
    if (!msisdn) {
        const heResponse = await SafaricomService.getMsisdnViaHeaderEnrichment();
        if (heResponse.msisdn) msisdn = heResponse.msisdn;
    }

    if (msisdn) {
        sessionStorage.setItem('msisdn', msisdn);
        const response = await SafaricomService.getUser(msisdn, store.state.brandConfig.productId);
        const user = response.data;
        const cleanedUser = cleanSafariComUser(user, store.state.brandConfig.revenuePartnerId, store.state.brandConfig.userAccessId);
        const isActive = user.Status === "ACTIVE" || user.Status === "PENDING";
        const hasDiySubscription = isActive ? false : await hasActiveSafaricomDiySubscription(msisdn);
        if (!isActive && !hasDiySubscription) return redirectToSafaricomSubscription(cleanedUser.msisdn);
        else {
            if (hasDiySubscription) {
                cleanedUser.status = 'Subscribed';
                cleanedUser.grantAccess = true;
            }
            const stats = await UserDataService.getUserStats(user.Msisdn || msisdn);
            store.commit('setUser', cleanedUser);
            store.commit('setUserStats', stats.data);
            if (urlMsisdnRaw) return next({name: 'LandingPage'});
            else {
                if (to.fullPath === "/") return next({name: 'LandingPage'});
                return next();
            }
        }
    } else return redirectToSafaricomSubscription();
}

async function hasActiveSafaricomDiySubscription(msisdn) {
    try {
        const response = await SafaricomService.queryActiveSubscription(msisdn);
        const body = response.data && response.data.body;
        return Array.isArray(body) && body.length > 0;
    } catch (error) {
        console.log('Safaricom DIY query-active-subscription failed:', error && error.message ? error.message : error);
        return false;
    }
}

async function telkomFlow(to, from, next) {
    const urlParams = new URLSearchParams(window.location.search);
    const urlMsisdn = urlParams.get('msisdn')
    const storedMsisdn = sessionStorage.getItem('msisdn');
    // const msisdn = urlParams.get('msisdn') ? urlParams.get('msisdn') : localStorage.getItem('msisdn');
    if (urlMsisdn || storedMsisdn) {
        const msisdn = storedMsisdn ? storedMsisdn : urlMsisdn
        sessionStorage.setItem('msisdn', msisdn)
        const response = await TelkomService.getUser(msisdn, store.state.brandConfig.productId);
        const user = response.data.user;
        const cleanedUser = cleanTelkomUser(user, store.state.brandConfig.revenuePartnerId, store.state.brandConfig.userAccessId)
        if (user.Status !== "ACTIVE" && user.Status !== "PENDING" && user.Status !== "SUSPENDED") return redirectToTelkomSubscription();
        else {
            const stats = await UserDataService.getUserStats(user.Msisdn);
            store.commit('setUser', cleanedUser);
            store.commit('setUserStats', stats.data);
            if (urlMsisdn) return next({name: 'LandingPage'})
            else {
                if (to.fullPath === "/") return next({name: 'LandingPage'});
                return next();
            }
        }
    }
    return redirectToTelkomSubscription();
}

function redirectToTelkomSubscription() {
    window.open('https://sdp-p-vas-payment.telkom.co.za/300000092', '_self')
}

async function redirectToSafaricomSubscription(msisdn) {
    const response = await DcbService.encryptUserMsisdn(msisdn)
    window.open(`https://ke-mplay.fantasyfootball.one/?e-msisdn=${response.encryptedMsisdn}`, '_self')
}


async function setUserData(token, to, next) {
    const user = await UserDataService.getUserStatus(`${token}`);
    if (!user) return redirectToLogin(next);

    localStorage.setItem('token', token);
    const stats = await UserDataService.getUserStats(user.data.msisdn);
    store.commit('setUser', user.data);
    store.commit('setUserStats', stats.data);

    if (user.data.grantAccess) {
        await LoginService.trackLogin(user.data.msisdn, store.state.brandConfig.revenuePartnerId);
        return redirectToProfile(next);
    }
    if (user.data.token === 'invalidToken') return invalidTokenRedirect(next);
    if (!user.data.grantAccess && stats) return redirectToSubscription(next);
    else return redirectToLogin(next);
}

function checkUserState() {
    if (store.state.termsAndConditionsOpened) return true;
    if (store.state.frequentlyAskedQuestionsOpened) return true;
    if (store.state.user && store.state.userLandingStats) return true;
    return false
}

async function encryptedMsisdnAuth(encryptedMsisdn, next, to) {
    const headerEnrichedLoginToken = await UserDataService.headerEnrichedLogin(encryptedMsisdn);
    await setUserData(headerEnrichedLoginToken, to, next);
    store.commit('setIsLoading', false);
}

function attemptEncryptionRedirect(uuid) {
    if (store.state.brandConfig.notUpstreamService) return;
    const encryptionConfig = store.state.brandConfig.encryptionKey;
    if (!uuid && !store.state.headerEnrichmentAttempted && isUpstreamHeaderEnrichemntBrand(store.state.brandConfig.brand)) window.open(`${encryptionConfig.enrichmentUrl}`, '_self');
}

async function upstreamEnrichedUserLogin(uuid, next, to) {
    const msisdn = await upstreamHeaderEnrichedLogin(uuid);
    const user = await UserDataService.getUser(msisdn);
    if (user.data.token) {
        await UserDataService.updateToken(user.data.token);
        const newUser = await UserDataService.getUser(msisdn);
        sessionStorage.setItem(`isUuidLogin`, true)
        await setUserData(newUser.data.token, to, next);
    }
}

function redirectToLogin(next) {
    store.commit('setIsLoading', false);
    if (store.state.brandConfig.brand === 'vc-za-en') return window.open(`${environmentConfig.entryPortalHost}`, '_self');
    if (store.state.brandConfig.brand === 'demo-arabic') return window.open(`https://ar.fantasy.penrosegaming.com/?fantasyToken=0F3D9469-D581-4511-ABA1-0FF65259B54F`, '_self');
    if (store.state.brandConfig.brand === 'saudi-uae-en') return window.open(`https://demo-en-fantasy-penrosegaming-com.migration.penrosesystems.com/?fantasyToken=0F3D9469-D581-4511-ABA1-0FF65259B54F`, '_self');
    if (store.state.brandConfig.brand === 'ais-tai-en') return window.open(`https://ais-fantasy-penrosegaming-com.migration.penrosesystems.com/?fantasyToken=0F3D9469-D581-4511-ABA1-0FF65259B54F`, '_self');
    if (store.state.brandConfig.brand === 'dtac-tai-en') return window.open(`https://dtac-fantasy-penrosegaming-com.migration.penrosesystems.com/?fantasyToken=0F3D9469-D581-4511-ABA1-0FF65259B54F`, '_self');
    if (store.state.brandConfig.brand === 'dtac-tai-tha') return window.open(`https://dtac-fantasy-penrosegaming-com.migration.penrosesystems.com/?fantasyToken=0F3D9469-D581-4511-ABA1-0FF65259B54F`, '_self');
    if (store.state.brandConfig.brand === 'demo-romanian') return window.open(`https://ro.fantasy.penrosegaming.com/?fantasyToken=0F3D9469-D581-4511-ABA1-0FF65259B54F`, '_self');
    else return next(`/login`);
}

function redirectToProfile(next) {
    store.commit('setIsLoading', false);
    return next('/profile');
}

function invalidTokenRedirect(next) {
    localStorage.removeItem("token");
    store.commit('setUser', undefined);
    store.commit('setUserStats', undefined);
    if (store.state.brandConfig.brand === 'vc-za-en') return window.open(`${environmentConfig.entryPortalHost}`, '_self');
    else redirectToLogin(next);
}

function redirectForMtnGhana() {
    return window.open(`http://op1.world-of-fun.com.gh/GMF-GHMTN-UPSTR/fantasygames1-en-doi-web`, '_self')
}

function redirectToSubscription(next) {
    if (store.state.brandConfig.brand === 'vc-za-en') return window.open(`${environmentConfig.entryPortalHost}`, '_self');
    else {
        store.commit('setIsLoading', false);
        if (this.brandConfig.brand === 'mtn-gha-en') return redirectForMtnGhana();
        else return next(`/subscription`);
    }
}

function isUpstreamHeaderEnrichemntBrand(currentBrand) {
    if (currentBrand === 'saf-ke-en') return false
    if (currentBrand === 'demo-um-en') return false
    if (currentBrand === 'demo-arabic') return false
    if (currentBrand === 'saudi-uae-en') return false
    if (currentBrand === 'vc-za-en') return false
    if (currentBrand === 'ais-tai-en') return false
    if (currentBrand === 'dtac-tai-en') return false
    if (currentBrand === 'dtac-tai-tha') return false
    if (currentBrand === 'ais-tai-tha') return false
    if (currentBrand === 'demo-romanian') return false
    return true
}

